import { useCallback, useState } from 'react'

import CompletionMethodModal from 'src/components/Overlays/CompletionMethodModal/CompletionMethodModal'
import type { CompletionMethodValues } from 'src/components/Overlays/CompletionMethodModal/CompletionMethodModal'
import { voeCompletionContext } from 'src/components/Overlays/CompletionMethodModal/voeCompletionContext'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { DocumentUserStatus } from 'src/graphql/types/documents'
import { VoEStatus } from 'src/graphql/types/verificationOfEmployments'

/** The verification a document is linked to (`Document.voe`). */
export interface DocumentVoELink {
  voeId: string
  status: VoEStatus | string
  companyName: string
  driverName?: string | null
  applicant?: { firstName?: string | null; lastName?: string | null } | null
  contactName?: string | null
  email?: string | null
  phoneNumber?: string | null
  faxNumber?: string | null
  dotNumber?: string | null
  platform?: string | null
  carrierRouting?: { preferredPlatform?: string | null } | null
}

/**
 * Completing a document that is linked to an open verification completes the
 * verification too, so the server needs to know how the answer arrived.
 */
export const needsVoECompletion = (
  voe: DocumentVoELink | null | undefined,
  userStatus: DocumentUserStatus | string | null | undefined,
  /** The document's status before this change; re-saving Completed is not a change. */
  currentStatus?: DocumentUserStatus | string | null
): voe is DocumentVoELink =>
  userStatus === DocumentUserStatus.COMPLETED &&
  currentStatus !== DocumentUserStatus.COMPLETED &&
  !!voe &&
  voe.status !== VoEStatus.COMPLETED

interface StatusChangeRequest {
  voe: DocumentVoELink | null | undefined
  userStatus: DocumentUserStatus | string
  /** The document's status before the change, when the surface knows it. */
  currentStatus?: DocumentUserStatus | string | null
  /**
   * The surface is still loading (or failed to load) which verification the
   * document is linked to. A Completed change then waits: sending it as if
   * nothing were linked would be refused by the server.
   */
  voeLinkPending?: boolean
  /** Called instead of sending while the link is pending, e.g. to retry it. */
  onVoELinkPending?: () => void
  /** Sends the update; `voeCompletion` is set only when the modal was shown. */
  send: (voeCompletion?: CompletionMethodValues) => void | Promise<void>
  /** The recruiter kept the current status; nothing was sent. */
  onCancel?: () => void
}

interface PendingCompletion {
  voe: DocumentVoELink
  send: StatusChangeRequest['send']
  onCancel?: () => void
}

/**
 * Gates a document status change: marking a document Completed while its
 * linked verification is still open first asks how the verification was
 * completed. Every other change is sent straight away. Render `modal`
 * somewhere in the calling component.
 */
const useVoEDocumentCompletionGate = () => {
  const [pending, setPending] = useState<PendingCompletion | null>(null)
  // Kept after closing so the modal text doesn't blank out while it animates.
  const [shownVoE, setShownVoE] = useState<DocumentVoELink | null>(null)

  const requestStatusChange = useCallback(
    ({
      voe,
      userStatus,
      currentStatus,
      voeLinkPending,
      onVoELinkPending,
      send,
      onCancel,
    }: StatusChangeRequest) => {
      const becomesCompleted =
        userStatus === DocumentUserStatus.COMPLETED &&
        currentStatus !== DocumentUserStatus.COMPLETED
      if (voeLinkPending && becomesCompleted) {
        toast(
          'Still checking whether this document belongs to a verification. Try again in a moment.',
          'error'
        )
        onVoELinkPending?.()
        onCancel?.()
        return
      }
      if (needsVoECompletion(voe, userStatus, currentStatus)) {
        setShownVoE(voe)
        setPending({ voe, send, onCancel })
        return
      }
      send()
    },
    []
  )

  const handleCancel = () => {
    const onCancel = pending?.onCancel
    setPending(null)
    onCancel?.()
  }

  const handleSubmit = (values: CompletionMethodValues) => {
    const send = pending?.send
    setPending(null)
    send?.(values)
  }

  const modal = (
    <CompletionMethodModal
      opened={!!pending}
      onClose={handleCancel}
      onSubmit={handleSubmit}
      submitLabel="Complete"
      cancelLabel="Keep current status"
      allowNoResponse={false}
      {...voeCompletionContext(shownVoE)}
    />
  )

  return { requestStatusChange, modal }
}

export default useVoEDocumentCompletionGate
